import React, { useEffect } from 'react';
import { Modal } from 'react-bootstrap';
import styles from './style.module.scss';
import classNames from 'classnames';
import Heading from 'shared/components/common/heading/Heading';
import CustomInput from 'shared/components/common/custom_input/CustomInput';
import CustomButton from 'shared/components/common/custom_button/CustomButton';
import CustomTextarea from 'shared/components/common/custom_text_area/CustomTextArea';
import { useSelector } from 'react-redux';
import { CreateQRCodeMutation, UpdateQRCodeMutation } from 'shared/services/qr_code_service/QRCodeService';
import OverlayLoading from 'shared/screen_loader/overlay_loader/OverlayLoading';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const QRCodeValidationSchema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    target_url: Yup.string()
        .required('Destination URL is required')
        .test('is-url', 'Please enter a valid URL (e.g. https://google.com or example.com)', (val) => {
            if (!val) return false;
            return true;
        }),
    code: Yup.string().matches(/^[a-zA-Z0-9_-]*$/, 'Code can only contain letters, numbers, hyphens, or underscores'),
    qr_type: Yup.string().oneOf(['dynamic', 'static']),
    description: Yup.string()
});

export default function AddQRCodeModal({ show, handleClose, isEdit = false, selectedItem }) {
    const { mutate: createMutate, isLoading: createLoading, isSuccess: createSuccess } = CreateQRCodeMutation();
    const { mutate: updateMutate, isLoading: updateLoading, isSuccess: updateSuccess } = UpdateQRCodeMutation();
    const {
        theme: { theme }
    } = useSelector((state) => state.root);

    useEffect(() => {
        if (show && isEdit && selectedItem) {
            setFieldValue('title', selectedItem.title || '');
            setFieldValue('target_url', selectedItem.target_url || '');
            setFieldValue('code', selectedItem.code || '');
            setFieldValue('qr_type', selectedItem.qr_type || 'dynamic');
            setFieldValue('description', selectedItem.description || '');
            setFieldValue('is_active', selectedItem.is_active !== undefined ? selectedItem.is_active : true);
        } else if (show && !isEdit) {
            formik.resetForm();
        }
    }, [show, isEdit, selectedItem]);

    useEffect(() => {
        if ((createSuccess || updateSuccess) && show) {
            onClose();
        }
    }, [createSuccess, updateSuccess]);

    const onClose = () => {
        handleClose();
        formik.resetForm();
    };

    const initialValues = {
        title: '',
        target_url: '',
        code: '',
        qr_type: 'dynamic',
        description: '',
        is_active: true
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initialValues,
        validationSchema: QRCodeValidationSchema,
        onSubmit: (values) => {
            const payload = {
                title: values.title.trim(),
                target_url: values.target_url.trim(),
                qr_type: values.qr_type,
                description: values.description ? values.description.trim() : '',
                is_active: values.is_active
            };
            if (values.code && values.code.trim() !== '') {
                payload.code = values.code.trim().toLowerCase();
            }

            if (isEdit && selectedItem) {
                updateMutate({
                    id: selectedItem._id,
                    payload
                });
            } else {
                createMutate(payload);
            }
        }
    });

    const { handleChange, setFieldValue, handleSubmit, values, touched, errors } = formik;

    return (
        <Modal show={show} onHide={onClose} size="md" centered>
            <Modal.Header className={styles.Header}>
                <Heading heading={isEdit ? 'Edit QR Code' : 'Create QR Code'} headingStyle={styles.AddLinkHeading} />
            </Modal.Header>
            <Modal.Body className={classNames(`${theme}ModalBackground`, styles.Body)}>
                <div className={classNames(styles.AddLinkContainer)}>
                    {/* QR Code Type Selector */}
                    <div className="mb-3">
                        <label className={classNames('form-label d-block', styles.CustomLabelStyle)}>
                            QR Code Mode / Type
                        </label>
                        <div className="row g-2">
                            <div className="col-6">
                                <div
                                    className={classNames(
                                        'p-2 border rounded pointer text-center h-100 d-flex flex-column justify-content-center',
                                        values.qr_type === 'dynamic'
                                            ? 'border-primary bg-primary bg-opacity-10 text-primary fw-bold'
                                            : 'border-secondary text-muted'
                                    )}
                                    onClick={() => setFieldValue('qr_type', 'dynamic')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="small">⚡ Dynamic (Editable)</div>
                                    <div style={{ fontSize: '0.72rem' }} className="fw-normal mt-1">
                                        Update target link anytime without re-printing
                                    </div>
                                </div>
                            </div>
                            <div className="col-6">
                                <div
                                    className={classNames(
                                        'p-2 border rounded pointer text-center h-100 d-flex flex-column justify-content-center',
                                        values.qr_type === 'static'
                                            ? 'border-success bg-success bg-opacity-10 text-success fw-bold'
                                            : 'border-secondary text-muted'
                                    )}
                                    onClick={() => setFieldValue('qr_type', 'static')}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <div className="small">🎯 Static (Direct URL)</div>
                                    <div style={{ fontSize: '0.72rem' }} className="fw-normal mt-1">
                                        Camera shows target URL directly on scan
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <CustomInput
                        label={'Title / Campaign Name *'}
                        placeholder={'e.g. Summer Promo, Product Catalog, Store Link'}
                        CustomInputStyle={classNames(`${theme}ItemBackground`, styles.CustomInputStyle)}
                        CustomLabelStyle={styles.CustomLabelStyle}
                        InputContainerStyle={styles.InputContainerStyle}
                        value={values.title}
                        onChange={handleChange('title')}
                        error={errors.title && touched.title ? errors.title : ''}
                    />
                    <CustomInput
                        label={'Destination Target URL *'}
                        placeholder={'https://yourwebsite.com/product/123'}
                        CustomInputStyle={classNames(`${theme}ItemBackground`, styles.CustomInputStyle)}
                        CustomLabelStyle={styles.CustomLabelStyle}
                        InputContainerStyle={styles.InputContainerStyle}
                        value={values.target_url}
                        onChange={handleChange('target_url')}
                        error={errors.target_url && touched.target_url ? errors.target_url : ''}
                    />

                    {values.qr_type === 'dynamic' && (
                        <CustomInput
                            label={isEdit ? 'Short Slug / Code (Permanent)' : 'Custom Short Slug / Code (Optional)'}
                            placeholder={isEdit ? '' : 'e.g. promo2026 (leave blank to auto-generate)'}
                            CustomInputStyle={classNames(`${theme}ItemBackground`, styles.CustomInputStyle)}
                            CustomLabelStyle={styles.CustomLabelStyle}
                            InputContainerStyle={styles.InputContainerStyle}
                            value={values.code}
                            onChange={handleChange('code')}
                            disabled={isEdit}
                            error={errors.code && touched.code ? errors.code : ''}
                        />
                    )}

                    <CustomTextarea
                        Label={'Description / Notes (Optional)'}
                        type={'text'}
                        Placeholder={'e.g. Printed on flyer packaging batch #4'}
                        CustomInputStyle={classNames(`${theme}ItemBackground`, styles.CustomInputStyle)}
                        CustomLabelStyle={styles.CustomLabelStyle}
                        InputContainerStyle={styles.InputContainerStyle}
                        Value={values.description}
                        OnChange={handleChange('description')}
                        error={errors.description && touched.description ? errors.description : ''}
                    />

                    {values.qr_type === 'dynamic' && (
                        <div className="d-flex align-items-center mb-3 mt-2">
                            <div className="form-check form-switch">
                                <input
                                    className="form-check-input pointer"
                                    type="checkbox"
                                    role="switch"
                                    id="qrActiveSwitch"
                                    checked={values.is_active}
                                    onChange={(e) => setFieldValue('is_active', e.target.checked)}
                                    style={{ width: '2.5rem', height: '1.3rem' }}
                                />
                                <label className={classNames('form-check-label ms-2 pointer', styles.CustomLabelStyle)} htmlFor="qrActiveSwitch">
                                    Active (Redirect scans immediately)
                                </label>
                            </div>
                        </div>
                    )}

                    <div className="d-flex justify-content-end mt-4">
                        <CustomButton text={'Cancel'} containerStyle={styles.CancelBtn} submitHandle={onClose} />
                        <CustomButton text={isEdit ? 'Save Changes' : 'Create QR Code'} containerStyle={styles.SubmitBtn} submitHandle={handleSubmit} />
                    </div>
                </div>
                {(createLoading || updateLoading) && <OverlayLoading />}
            </Modal.Body>
        </Modal>
    );
}
